import { Colors, Lightning } from '@lightningjs/sdk';
import { HoverableListItem } from 'components/common/HoverableListItem';
import { constants } from 'aliases';
import { getImageTextureObj } from 'support/generalUtils';
import TextLabel from 'components/common/TextLabel';

export interface ListSelectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  label: string;
  endIcon?: string;
  isSelected: boolean;
  Content: {
    LabelText: typeof TextLabel;
    Indicator: Lightning.textures.ImageTexture;
    EndIcon: Lightning.textures.ImageTexture;
  };
  Background: Lightning.Component;
  BottomBorder: Lightning.Component;
}

const CONTENT_HEIGHT = 72;
const CONTENT_WIDTH = 640;

const INDICATOR_WIDTH = 16;
const INDICATOR_HEIGHT = 22;

const END_ICON_WIDTH = 48;
const END_ICON_HEIGHT = 22;
const END_ICON_MARGIN_LEFT = 8;

export default class ListSelection
  extends HoverableListItem<ListSelectionTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<ListSelectionTemplateSpec>
{
  private _indicatorDarkImg = 'static/images/setting-menu-arrow-dark.svg';
  private _indicatorLightImg = 'static/images/setting-menu-arrow-light.svg';

  private _Content = this.getByRef('Content')!;
  private _Indicator = this._Content.getByRef('Indicator')!;
  private _LabelText = this._Content.getByRef('LabelText')!;
  private _Background = this.getByRef('Background')!;
  protected _EndIcon = this._Content.getByRef('EndIcon')!;

  private _label = '';
  private _isSelected = false;

  get label() {
    return this._label;
  }

  set label(label: string) {
    this._label = label;
    this._LabelText.patch({ text: { text: label } });
  }

  get isSelected() {
    return this._isSelected;
  }

  set isSelected(isSelected: boolean) {
    this._isSelected = isSelected;
    this.backgroundUpdate(isSelected);
  }

  set endIcon(value: string) {
    if (value) {
      this._EndIcon.patch({
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
        ...getImageTextureObj(value, END_ICON_WIDTH, END_ICON_HEIGHT),
        w: END_ICON_WIDTH,
        h: END_ICON_HEIGHT,
        flexItem: { marginLeft: END_ICON_MARGIN_LEFT },
      });
    }
  }

  get title() {
    return this.label ?? '';
  }

  static get height() {
    return CONTENT_HEIGHT;
  }
  static get width() {
    return CONTENT_WIDTH;
  }

  static override _template(): Lightning.Component.Template<ListSelectionTemplateSpec> {
    return {
      Content: {
        w: CONTENT_WIDTH,
        h: this.height,
        flex: {
          direction: 'row',
          alignItems: 'center',
        },
        Indicator: {
          zIndex: constants.ui.zIndex.foregroundLayer + 2,
          ...getImageTextureObj(
            'static/images/setting-menu-arrow-light.svg',
            16,
            22,
          ),
          x: CONTENT_WIDTH - INDICATOR_WIDTH - 10,
          alpha: 1,
          w: INDICATOR_WIDTH,
          h: INDICATOR_HEIGHT,
        },
        LabelText: {
          type: TextLabel,
          zIndex: constants.ui.zIndex.foregroundLayer + 2,
          flexItem: { alignSelf: 'center' },
          text: {
            fontSize: 22,
            lineHeight: 24,
            textColor: Colors('text').get(),
            verticalAlign: 'bottom',
          },
        },
        EndIcon: {},
      },
      Background: {
        rect: true,
        zIndex: constants.ui.zIndex.foregroundLayer + 1,
        w: w => w,
        h: h => h,
        color: Colors('appBackground').get(),
      },
      BottomBorder: {
        rect: true,
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
        x: 0,
        y: CONTENT_HEIGHT,
        w: CONTENT_WIDTH,
        h: 1,
        color: Colors('white').get(),
      },
    };
  }

  override _focus() {
    this._Background.patch({ color: Colors('white').get() });
    this._LabelText.patch({
      text: {
        textColor: Colors('black').get(),
      },
    });
    this._Indicator.patch(getImageTextureObj(this._indicatorDarkImg, 16, 22));
  }

  override _unfocus() {
    this.backgroundUpdate(this._isSelected);
    this._LabelText.patch({ text: { textColor: Colors('white').get() } });
    this._Indicator.patch(getImageTextureObj(this._indicatorLightImg, 16, 22));
  }

  override _handleHover(target: Lightning.Element) {
    this.collectionWrapper?.signal('$onHover', this.collectionWrapper);
    return super._handleHover(target);
  }

  private backgroundUpdate(selected: boolean) {
    if (!this.hasFocus()) {
      this._Background.patch({
        color: selected ? Colors('grey').get() : Colors('appBackground').get(),
      });
    }
  }
}
